import React, { createContext, useContext, useState, useEffect } from 'react';
import { calculateSkillLevel, getUIPreferences, SKILL_LEVELS } from '../services/userSkillLevel';

const UserSkillContext = createContext();

export const useUserSkill = () => {
  const context = useContext(UserSkillContext);
  if (!context) {
    throw new Error('useUserSkill must be used within UserSkillProvider');
  }
  return context;
};

export const UserSkillProvider = ({ children }) => {
  const [skillLevel, setSkillLevel] = useState(SKILL_LEVELS.BEGINNER);
  const [uiPreferences, setUiPreferences] = useState(getUIPreferences(SKILL_LEVELS.BEGINNER));
  const [userActivity, setUserActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user activity and calculate skill level
  useEffect(() => {
    loadUserActivity();
  }, []);

  const loadUserActivity = async () => {
    try {
      // TODO: Replace with actual API call
      // For now, use mock data from localStorage or default
      const mockActivity = {
        totalCampaigns: parseInt(localStorage.getItem('totalCampaigns') || '0'),
        totalContent: parseInt(localStorage.getItem('totalContent') || '0'),
        totalAnalytics: parseInt(localStorage.getItem('totalAnalytics') || '0'),
        accountAge: parseInt(localStorage.getItem('accountAge') || '1'),
        lastLoginDays: 0
      };

      setUserActivity(mockActivity);
      
      const level = calculateSkillLevel(mockActivity);
      setSkillLevel(level);
      setUiPreferences(getUIPreferences(level));
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading user activity:', error);
      setIsLoading(false);
    }
  };

  // Update activity (call this when user creates campaigns, content, etc.)
  const updateActivity = (activityType, increment = 1) => {
    const newActivity = { ...userActivity };
    
    switch (activityType) {
      case 'campaign':
        newActivity.totalCampaigns = (newActivity.totalCampaigns || 0) + increment;
        localStorage.setItem('totalCampaigns', newActivity.totalCampaigns.toString());
        break;
      case 'content':
        newActivity.totalContent = (newActivity.totalContent || 0) + increment;
        localStorage.setItem('totalContent', newActivity.totalContent.toString());
        break;
      case 'analytics':
        newActivity.totalAnalytics = (newActivity.totalAnalytics || 0) + increment;
        localStorage.setItem('totalAnalytics', newActivity.totalAnalytics.toString());
        break;
      default:
        break;
    }

    setUserActivity(newActivity);
    
    // Recalculate skill level
    const newLevel = calculateSkillLevel(newActivity);
    if (newLevel !== skillLevel) {
      setSkillLevel(newLevel);
      setUiPreferences(getUIPreferences(newLevel));
      
      // Show notification about skill level change
      if (newLevel === SKILL_LEVELS.INTERMEDIATE) {
        // TODO: Show toast notification
        console.log('🎉 You\'ve unlocked Intermediate features!');
      } else if (newLevel === SKILL_LEVELS.ADVANCED) {
        console.log('🚀 You\'ve unlocked Advanced features!');
      }
    }
  };

  // Manual override (for testing or user preference)
  const setSkillLevelManually = (level) => {
    setSkillLevel(level);
    setUiPreferences(getUIPreferences(level));
    localStorage.setItem('manualSkillLevel', level);
  };

  const value = {
    skillLevel,
    uiPreferences,
    userActivity,
    isLoading,
    updateActivity,
    setSkillLevel: setSkillLevelManually,
    refreshSkillLevel: loadUserActivity
  };

  return (
    <UserSkillContext.Provider value={value}>
      {children}
    </UserSkillContext.Provider>
  );
};

export default UserSkillContext;
